import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import ImageModel from "../models/ImageModel";

/**
 * Resolve an artefact number to the media that actually exists, trying formats
 * in order (jpg → jpeg → png → mp4). Returns the current candidate ({type,url})
 * and an onError handler that advances to the next candidate; once every format
 * has failed, `errored` is true.
 *
 * A null/invalid key yields no candidate (e.g. before the counter has loaded).
 */
export function useMediaFallback(imageKey) {
  const candidates = useMemo(
    () => (Number.isFinite(imageKey) && imageKey >= 1 ? ImageModel.mediaCandidates(imageKey) : []),
    [imageKey],
  );
  const [attempt, setAttempt] = useState(0);
  const [errored, setErrored] = useState(false);
  // Mirrors `attempt` so onError can decide in the callback body. Deciding
  // inside a setState updater would make the updater impure — React may invoke
  // it twice (StrictMode), firing the decision more than once.
  const attemptRef = useRef(0);

  useEffect(() => {
    attemptRef.current = 0;
    setAttempt(0);
    setErrored(false);
  }, [candidates]);

  const onError = useCallback(() => {
    const next = attemptRef.current + 1;
    if (next < candidates.length) {
      attemptRef.current = next;
      setAttempt(next);
    } else {
      setErrored(true);
    }
  }, [candidates.length]);

  const current = errored ? null : candidates[attempt] || null;
  return { current, errored, onError };
}

/**
 * Ref callback ensuring a grid/looping video actually autoplays: `muted` MUST be
 * set as a property (not just the attribute) for autoplay to be allowed, and we
 * nudge play() in case the browser didn't start it.
 */
export function primeVideo(el) {
  if (!el) return;
  el.muted = true;
  el.defaultMuted = true;
  const p = el.play();
  if (p && typeof p.catch === "function") p.catch(() => {});
}

/** Has this element already got something to paint? */
function isSettled(el) {
  if (!el) return false;
  // HAVE_CURRENT_DATA or better: a frame exists, so the poster/skeleton can go.
  if (el.tagName === "VIDEO") return el.readyState >= 2;
  return el.complete && el.naturalWidth > 0;
}

/**
 * Track whether the current media is ready to reveal.
 *
 * Media already in the browser cache can FINISH LOADING BEFORE React attaches
 * `onLoad`/`onLoadedData`, so that event never fires and a caller waiting on it
 * waits forever — the gallery card shimmers as a skeleton with the fully
 * downloaded image sitting at opacity 0 underneath. This is why the newest
 * artefact stalled as the first gallery tile in Chrome: the home page had just
 * cached it, so it was the one tile that never touched the network.
 *
 * Two complementary paths, both idempotent:
 *   - `mediaRef` inspects the element's own readiness when it is attached,
 *     catching media that was ready before React could listen;
 *   - `markReady` is the normal event handler for the network path.
 *
 * `srcKey` is the current media URL. Changing it resets and re-checks, so a
 * format fallback (jpg → mp4) landing on a cached file is caught too.
 */
export function useMediaReady(srcKey) {
  const [ready, setReady] = useState(false);
  const elRef = useRef(null);

  const markReady = useCallback(() => setReady(true), []);

  const mediaRef = useCallback((el) => {
    elRef.current = el;
    if (!el) return;
    if (el.tagName === "VIDEO") primeVideo(el);
    if (isSettled(el)) setReady(true);
  }, []);

  // Runs after refs are attached in the same commit, and again whenever the
  // source changes, so a swapped-in cached file is never missed.
  useLayoutEffect(() => {
    setReady(false);
    if (!srcKey) return;
    if (isSettled(elRef.current)) setReady(true);
  }, [srcKey]);

  return { ready, markReady, mediaRef };
}
